import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { marketplaceAPI, uploadAPI } from '../lib/api';
import { extUrl } from '../lib/url';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, ShoppingBag, Plus, Loader2, DollarSign, Tag, MessageCircle,
  Heart, SortAsc, Camera, X, Shield, Video, Crown, Star, Award, ArrowRight, Check,
  ExternalLink, Store, Globe
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useCommunity } from '../contexts/CommunityContext';
import LocationAutocomplete from '../components/LocationAutocomplete';
import usePageTitle from '../hooks/usePageTitle';

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'textbooks', label: 'Textbooks' },
  { value: 'greek', label: 'Greek Paraphernalia' },
  { value: 'merch', label: 'School Merch' },
  { value: 'services', label: 'Services' },
  { value: 'beauty', label: 'Beauty' },
  { value: 'food', label: 'Food & Catering' },
];

const Marketplace = () => {
  usePageTitle('Marketplace');
  const { user } = useAuth();
  const { institutionFilter } = useCommunity();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [listingTypeFilter, setListingTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [createMode, setCreateMode] = useState('for_sale'); // for_sale or advertisement
  const [newItem, setNewItem] = useState({
    title: '', description: '', price: '', category: 'electronics', condition: 'good', photos: [], website_url: ''
  });
  const [itemPhoto, setItemPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [itemVideo, setItemVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [vendorStatus, setVendorStatus] = useState(null);
  const [advertiserStatus, setAdvertiserStatus] = useState(null);
  const [showVendorPlans, setShowVendorPlans] = useState(false);
  const [showAdvertiserPlan, setShowAdvertiserPlan] = useState(false);
  const [showSellAdvertise, setShowSellAdvertise] = useState(false);

  useEffect(() => { fetchItems(); }, [category, institutionFilter.institution, listingTypeFilter]);
  useEffect(() => {
    marketplaceAPI.vendorStatus().then(r => setVendorStatus(r.data)).catch(() => {});
    marketplaceAPI.advertiserStatus().then(r => setAdvertiserStatus(r.data)).catch(() => {});
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { ...institutionFilter };
      if (search) params.search = search;
      if (locationSearch) params.location = locationSearch;
      if (category !== 'all') params.category = category;
      if (listingTypeFilter !== 'all') params.listing_type = listingTypeFilter;
      const res = await marketplaceAPI.list(params);
      let data = Array.isArray(res.data) ? res.data : [];
      if (sortBy === 'price_low') data.sort((a, b) => (a.price || 0) - (b.price || 0));
      else if (sortBy === 'price_high') data.sort((a, b) => (b.price || 0) - (a.price || 0));
      setItems(data);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setItemPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Max 100MB for video'); return; }
    setItemVideo(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleCreate = async () => {
    const isAd = createMode === 'advertisement';
    if (!newItem.title) { toast.error('Title is required'); return; }
    if (!isAd && !newItem.price) { toast.error('Price is required'); return; }
    if (isAd && !newItem.website_url) { toast.error('Website URL is required for advertisements'); return; }

    setCreating(true);
    try {
      let photos = [];
      if (itemPhoto) {
        const uploadRes = await uploadAPI.marketplaceImage(itemPhoto);
        photos = [uploadRes.data.url];
      }
      let video_url = null;
      if (itemVideo) {
        const videoUploadRes = await uploadAPI.marketplaceImage(itemVideo);
        video_url = videoUploadRes.data.url;
      }
      const payload = {
        title: newItem.title,
        description: newItem.description,
        category: newItem.category,
        photos,
        listing_type: createMode,
      };
      if (isAd) {
        payload.website_url = newItem.website_url;
        payload.price = 0;
        payload.condition = 'new';
      } else {
        payload.price = parseFloat(newItem.price);
        payload.condition = newItem.condition;
      }
      if (video_url) payload.video_url = video_url;
      const res = await marketplaceAPI.create(payload);
      setItems([res.data, ...items]);
      setShowCreate(false);
      resetCreateForm();
      toast.success(isAd ? 'Advertisement listed!' : 'Item listed!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to list item';
      if (detail.includes('limit') && !isAd) {
        toast.error(detail, { duration: 6000 });
        setShowCreate(false);
        setShowVendorPlans(true);
      } else if (detail.includes('Advertiser subscription')) {
        toast.error(detail, { duration: 6000 });
        setShowCreate(false);
        setShowAdvertiserPlan(true);
      } else {
        toast.error(detail);
      }
    }
    finally { setCreating(false); }
  };

  const resetCreateForm = () => {
    setNewItem({ title: '', description: '', price: '', category: 'electronics', condition: 'good', photos: [], website_url: '' });
    setItemPhoto(null);
    setPhotoPreview(null);
    setItemVideo(null);
    setVideoPreview(null);
    setCreateMode('for_sale');
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const isAd = (item) => item.listing_type === 'advertisement';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Marketplace</h1>
            <p className="text-slate-gray mt-1">Buy, sell, and discover alumni businesses</p>
          </div>
          <Button onClick={() => setShowSellAdvertise(true)} className="bg-dark-slate text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />List / Sell / Advertise
          </Button>
        </div>

        {/* Listing limit banner */}
        {vendorStatus && !vendorStatus.vendor_tier && (
          <div className={`rounded-2xl p-4 mb-4 flex items-center justify-between ${
            vendorStatus.yearly_listings >= 8 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'
          }`}>
            <div className="flex items-center gap-3">
              <ShoppingBag className={`w-5 h-5 ${vendorStatus.yearly_listings >= 8 ? 'text-amber-500' : 'text-slate-gray'}`} />
              <div>
                <p className="text-sm font-medium text-dark-slate">
                  {vendorStatus.yearly_listings} of {vendorStatus.yearly_limit} free listings used this year
                </p>
                <p className="text-xs text-slate-gray">
                  {vendorStatus.yearly_listings >= vendorStatus.yearly_limit
                    ? 'Limit reached — upgrade to keep selling!'
                    : vendorStatus.yearly_listings >= 8
                    ? 'Almost at your limit — consider upgrading'
                    : 'Upgrade to a Vendor plan for unlimited listings'}
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowVendorPlans(true)} className="rounded-full text-xs">
              View Plans
            </Button>
          </div>
        )}
        {vendorStatus?.vendor_tier && (
          <div className="rounded-2xl p-3 mb-4 bg-gradient-to-r from-slate-50 to-amber-50/30 border border-amber-200/30 flex items-center gap-3">
            <span className="text-lg">{vendorStatus.badge === 'gold' ? '🥇' : vendorStatus.badge === 'silver' ? '🥈' : '🥉'}</span>
            <p className="text-sm font-medium text-dark-slate">
              {vendorStatus.badge?.charAt(0).toUpperCase() + vendorStatus.badge?.slice(1)} Vendor
              {vendorStatus.vendor_status === 'trial' && <span className="text-xs text-amber-600 ml-2">(Trial)</span>}
            </p>
          </div>
        )}
        {advertiserStatus?.is_active && (
          <div className="rounded-2xl p-3 mb-4 bg-gradient-to-r from-purple-50 to-fuchsia-50/30 border border-purple-200/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg">🏪</span>
              <p className="text-sm font-medium text-dark-slate">
                Marketplace Advertiser
                {advertiserStatus.advertiser_status === 'trial' && <span className="text-xs text-purple-600 ml-2">(Trial)</span>}
                <span className="text-xs text-slate-gray ml-2">{advertiserStatus.ad_listings} / {advertiserStatus.ad_limit} ads</span>
              </p>
            </div>
          </div>
        )}

        {/* Vendor Plans Dialog */}
        <Dialog open={showVendorPlans} onOpenChange={setShowVendorPlans}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate">Vendor Plans</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Upgrade to sell more and stand out in the marketplace.</p>
            <div className="space-y-3">
              {[
                { id: 'vendor_bronze', name: 'Bronze', price: 10, icon: '🥉', color: 'amber-700', bg: 'amber-50', perks: ['50 listings/month', 'Verified seller badge', 'Priority in search'] },
                { id: 'vendor_silver', name: 'Silver', price: 25, icon: '🥈', color: 'slate-500', bg: 'slate-50', perks: ['Unlimited listings', 'Featured badge', 'Boosted in search', 'Priority support'] },
                { id: 'vendor_gold', name: 'Gold', price: 50, icon: '🥇', color: 'amber-500', bg: 'amber-50', perks: ['Unlimited listings', 'Premium gold badge', 'Top of all listings', 'Homepage spotlight', 'Seller analytics'] },
              ].map(plan => (
                <div key={plan.id} className={`rounded-2xl p-5 border-2 ${vendorStatus?.vendor_tier === plan.name.toLowerCase() ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'} transition-colors`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{plan.icon}</span>
                      <div>
                        <h3 className="font-heading font-bold text-lg text-dark-slate">{plan.name}</h3>
                        <p className="text-xs text-slate-gray">14-day free trial</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-2xl text-dark-slate">${plan.price}</p>
                      <p className="text-xs text-slate-gray">/month</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 mb-4">
                    {plan.perks.map(perk => (
                      <li key={perk} className="flex items-center gap-2 text-sm text-slate-gray">
                        <Check className="w-4 h-4 text-mint-fresh flex-shrink-0" />{perk}
                      </li>
                    ))}
                  </ul>
                  {vendorStatus?.vendor_tier === plan.name.toLowerCase() ? (
                    <Button disabled className="w-full rounded-full bg-slate-100 text-slate-500">Current Plan</Button>
                  ) : (
                    <Button
                      onClick={async () => {
                        try {
                          const res = await marketplaceAPI.vendorSubscribe({ plan: plan.id, origin_url: window.location.origin });
                          if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                        } catch (e) { toast.error(e.response?.data?.detail || 'Subscription failed'); }
                      }}
                      className="w-full rounded-full bg-dark-slate text-white hover:bg-slate-800"
                    >
                      Start Free Trial <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Advertiser Plan Dialog */}
        <Dialog open={showAdvertiserPlan} onOpenChange={setShowAdvertiserPlan}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate">Advertise Your Business</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Showcase your business to the alumni community. Drive traffic to your website with up to 20 storefront listings in the marketplace.</p>
            <div className="rounded-2xl p-5 border-2 border-purple-300 bg-purple-50/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏪</span>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate">Marketplace Advertiser</h3>
                    <p className="text-xs text-slate-gray">14-day free trial</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-2xl text-dark-slate">$10</p>
                  <p className="text-xs text-slate-gray">/month</p>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Up to 20 advertisement listings',
                  '"Visit Website" button on each listing',
                  'Storefront badge in marketplace',
                  'Visible alongside all marketplace items',
                  'Showcase your full product catalog',
                  'Drive traffic to your own website',
                ].map(perk => (
                  <li key={perk} className="flex items-center gap-2 text-sm text-slate-gray">
                    <Check className="w-4 h-4 text-purple-500 flex-shrink-0" />{perk}
                  </li>
                ))}
              </ul>
              {advertiserStatus?.is_active ? (
                <Button disabled className="w-full rounded-full bg-slate-100 text-slate-500">Current Plan</Button>
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      const res = await marketplaceAPI.advertiserSubscribe({ origin_url: window.location.origin });
                      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                    } catch (e) { toast.error(e.response?.data?.detail || 'Subscription failed'); }
                  }}
                  className="w-full rounded-full bg-purple-600 text-white hover:bg-purple-700"
                >
                  Start Free Trial <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* List / Sell / Advertise Comparison Dialog */}
        <Dialog open={showSellAdvertise} onOpenChange={setShowSellAdvertise}>
          <DialogContent className="rounded-2xl max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate text-center">How would you like to list?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray text-center mb-6">Choose the option that best fits what you're looking to do.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              {/* Free Listing */}
              <div className="rounded-2xl p-5 border-2 border-emerald-200 bg-emerald-50/20 flex flex-col">
                {/* Graphic */}
                <div className="w-full h-28 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-50 flex items-center justify-center mb-4 overflow-hidden">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-emerald-100">
                      <Tag className="w-8 h-8 text-emerald-500" />
                    </div>
                    <div className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">FREE</div>
                    <div className="absolute -bottom-1 -left-2 w-6 h-6 rounded-full bg-emerald-200/60 flex items-center justify-center">
                      <ShoppingBag className="w-3 h-3 text-emerald-600" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">List an Item</h3>
                </div>
                <p className="text-sm text-slate-gray mb-4">
                  Selling a couch, laptop, textbooks, or anything else? List it for free and sell directly to other alumni.
                </p>
                <p className="text-xs font-semibold text-dark-slate mb-3">What you get:</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {[
                    `Up to ${vendorStatus?.yearly_limit || 10} free listings per year`,
                    'Add photos and descriptions',
                    'Message buyers directly',
                    'Visible to the entire alumni network',
                    'No fees or commissions',
                  ].map(perk => (
                    <li key={perk} className="flex items-start gap-2 text-xs text-slate-gray">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />{perk}
                    </li>
                  ))}
                </ul>
                <div className="mb-3">
                  <p className="text-sm font-semibold text-dark-slate"><span className="text-emerald-600">Free</span> — no subscription needed</p>
                  {vendorStatus && !vendorStatus.vendor_tier && (
                    <p className="text-[11px] text-slate-gray mt-0.5">{vendorStatus.yearly_listings} of {vendorStatus.yearly_limit} listings used this year</p>
                  )}
                </div>
                <Button
                  onClick={() => { setShowSellAdvertise(false); resetCreateForm(); setCreateMode('for_sale'); setShowCreate(true); }}
                  className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700 mt-auto"
                >
                  List for Free <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Vendor */}
              <div className="rounded-2xl p-5 border-2 border-amber-200 bg-amber-50/20 flex flex-col">
                {/* Graphic */}
                <div className="w-full h-28 rounded-xl bg-gradient-to-br from-amber-100 to-yellow-50 flex items-center justify-center mb-4 overflow-hidden">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-amber-100">
                      <Store className="w-8 h-8 text-amber-500" />
                    </div>
                    <div className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">PRO</div>
                    <div className="absolute -bottom-1 -left-2 w-6 h-6 rounded-full bg-amber-200/60 flex items-center justify-center">
                      <Shield className="w-3 h-3 text-amber-600" />
                    </div>
                    <div className="absolute -bottom-1 -right-3 w-5 h-5 rounded-full bg-yellow-200/60 flex items-center justify-center">
                      <Star className="w-2.5 h-2.5 text-amber-600" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">Vendor</h3>
                  <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] px-1.5">Business</Badge>
                </div>
                <p className="text-sm text-slate-gray mb-4">
                  Run a business? Sell products directly through GradLink with unlimited listings, a verified badge, and priority placement.
                </p>
                <p className="text-xs font-semibold text-dark-slate mb-3">Best for:</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {['Unlimited product listings', 'Verified seller badge', 'Priority placement in search', 'Sell physical or digital products', 'Analytics dashboard'].map(perk => (
                    <li key={perk} className="flex items-start gap-2 text-xs text-slate-gray">
                      <Check className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />{perk}
                    </li>
                  ))}
                </ul>
                <p className="text-sm font-semibold text-dark-slate mb-3">Plans from <span className="text-amber-600">$10/mo</span></p>
                <Button
                  onClick={() => { setShowSellAdvertise(false); setShowVendorPlans(true); }}
                  className="w-full rounded-full bg-amber-500 text-white hover:bg-amber-600 mt-auto"
                >
                  View Vendor Plans <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* Advertiser */}
              <div className="rounded-2xl p-5 border-2 border-purple-200 bg-purple-50/20 flex flex-col">
                {/* Graphic */}
                <div className="w-full h-28 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-50 flex items-center justify-center mb-4 overflow-hidden">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-white shadow-lg flex items-center justify-center border border-purple-100">
                      <Globe className="w-8 h-8 text-purple-500" />
                    </div>
                    <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">ADS</div>
                    <div className="absolute -bottom-1 -left-2 w-6 h-6 rounded-full bg-purple-200/60 flex items-center justify-center">
                      <ExternalLink className="w-3 h-3 text-purple-600" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading font-bold text-lg text-dark-slate">Advertiser</h3>
                  <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px] px-1.5">Brand</Badge>
                </div>
                <p className="text-sm text-slate-gray mb-4">
                  Showcase your business and drive traffic to your own website. Build brand awareness with the alumni community.
                </p>
                <p className="text-xs font-semibold text-dark-slate mb-3">Best for:</p>
                <ul className="space-y-1.5 mb-4 flex-1">
                  {['Promote your existing business', 'Drive traffic to your website', 'Showcase a product catalog', 'Build brand awareness with alumni', 'Up to 20 ad listings'].map(perk => (
                    <li key={perk} className="flex items-start gap-2 text-xs text-slate-gray">
                      <Check className="w-3.5 h-3.5 text-purple-500 flex-shrink-0 mt-0.5" />{perk}
                    </li>
                  ))}
                </ul>
                <p className="text-sm font-semibold text-dark-slate mb-3"><span className="text-purple-600">$10/mo</span> — up to 20 listings</p>
                <Button
                  onClick={() => { setShowSellAdvertise(false); setShowAdvertiserPlan(true); }}
                  className="w-full rounded-full bg-purple-600 text-white hover:bg-purple-700 mt-auto"
                >
                  Start Advertising <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

            </div>
            <p className="text-[11px] text-slate-gray text-center mt-3">Vendor and Advertiser plans include a 14-day free trial. Cancel anytime.</p>
          </DialogContent>
        </Dialog>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
            <Input placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchItems()} className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex-1 sm:max-w-[200px]">
            <LocationAutocomplete
              value={locationSearch}
              onChange={setLocationSearch}
              onSelect={(loc) => { setLocationSearch(loc); setTimeout(fetchItems, 100); }}
              placeholder="City or zip..."
              className="h-11 rounded-xl bg-white border-slate-200"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); }}>
            <SelectTrigger className="h-11 w-[150px] rounded-xl bg-white border-slate-200">
              <SortAsc className="w-4 h-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low</SelectItem>
              <SelectItem value="price_high">Price: High</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
          {CATEGORIES.map(cat => (
            <button key={cat.value} onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                category === cat.value ? 'bg-dark-slate text-white' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
              }`}>{cat.label}</button>
          ))}
        </div>

        {/* Listing type filter */}
        <div className="flex gap-2 mb-6">
          {[
            { value: 'all', label: 'Everything' },
            { value: 'for_sale', label: 'For Sale' },
            { value: 'advertisement', label: 'Storefronts' },
          ].map(f => (
            <button key={f.value} onClick={() => setListingTypeFilter(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                listingTypeFilter === f.value ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
              }`}>
              {f.value === 'advertisement' && <span className="mr-1">🏪</span>}
              {f.label}
            </button>
          ))}
        </div>

        {/* Items Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <ShoppingBag className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No items found</h3>
            <p className="text-slate-gray">Be the first to list something!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(item => (
              <div key={item.item_id}
                className={`bg-white rounded-2xl border shadow-ambient hover:shadow-hover transition-shadow overflow-hidden cursor-pointer ${
                  isAd(item) ? 'border-purple-200/60' : 'border-slate-100'
                }`}
                onClick={() => setSelected(item)}>
                <div className="relative aspect-square bg-slate-100">
                  {item.photos?.[0] ? (
                    <img src={item.photos[0]} alt={item.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      {isAd(item) ? <Store className="w-10 h-10 text-purple-300" /> : <ShoppingBag className="w-10 h-10 text-slate-300" />}
                    </div>
                  )}
                  {item.is_sold && !isAd(item) && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Badge className="bg-red-500 text-white text-lg">SOLD</Badge>
                    </div>
                  )}
                  {isAd(item) && (
                    <div className="absolute top-2 left-2">
                      <Badge className="text-[10px] bg-purple-600 text-white">🏪 Storefront</Badge>
                    </div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); toggleFavorite(item.item_id); }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center">
                    <Heart className={`w-3.5 h-3.5 ${favorites.has(item.item_id) ? 'text-rose-500 fill-current' : 'text-slate-gray'}`} />
                  </button>
                </div>
                <div className="p-3">
                  <p className="font-semibold text-dark-slate text-sm line-clamp-1">{item.title}</p>
                  {isAd(item) ? (
                    <p className="text-sm font-medium text-purple-600 flex items-center gap-1 mt-0.5">
                      <Globe className="w-3.5 h-3.5" />Visit Website
                    </p>
                  ) : (
                    <p className="text-lg font-bold text-dark-slate">${item.price?.toLocaleString()}</p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-[10px] bg-slate-100">{item.category}</Badge>
                      {item.seller_badge === 'gold' && <Badge className="text-[10px] bg-amber-400 text-white">🥇 Gold</Badge>}
                      {item.seller_badge === 'silver' && <Badge className="text-[10px] bg-slate-400 text-white">🥈 Silver</Badge>}
                      {item.seller_badge === 'bronze' && <Badge className="text-[10px] bg-amber-600 text-white">🥉 Bronze</Badge>}
                    </div>
                    {item.institution && <span className="text-[10px] text-slate-gray">{item.institution}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Detail Modal */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="max-w-md rounded-2xl">
            {selected && (
              <>
                {selected.photos?.[0] && <img src={selected.photos[0]} alt={selected.title} className="w-full h-64 object-cover rounded-xl mb-4" />}
                {selected.video_url && (
                  <video
                    src={selected.video_url}
                    controls
                    className="w-full rounded-xl mb-4 max-h-64 bg-black"
                  />
                )}
                <DialogHeader><DialogTitle className="font-heading text-xl">{selected.title}</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-3">
                  {isAd(selected) ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700">🏪 Storefront Listing</Badge>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-dark-slate">${selected.price?.toLocaleString()}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{selected.category}</Badge>
                    {!isAd(selected) && selected.condition && <Badge variant="secondary" className="bg-blue-50 text-blue-700">{selected.condition}</Badge>}
                    {selected.is_sold && !isAd(selected) && <Badge className="bg-red-500 text-white">SOLD</Badge>}
                  </div>
                  {selected.description && <p className="text-slate-gray text-sm whitespace-pre-wrap">{selected.description}</p>}
                  {selected.seller_name && (
                    <div className={`rounded-xl p-3 text-sm ${isAd(selected) ? 'bg-purple-50' : 'bg-mint-fresh/20'}`}>
                      {isAd(selected) ? 'Listed by' : 'Sold by'} <span className="font-semibold text-dark-slate">{selected.seller_name}</span>
                      {selected.institution && <span className="text-slate-gray"> from {selected.institution}</span>}
                    </div>
                  )}
                  {isAd(selected) && selected.website_url ? (
                    <a href={extUrl(selected.website_url)} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full h-11 bg-purple-600 text-white rounded-full hover:bg-purple-700">
                        <ExternalLink className="w-4 h-4 mr-2" />Visit Website
                      </Button>
                    </a>
                  ) : (
                    <>
                      {/* Stripe Trust Badge */}
                      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                        <Shield className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-indigo-700 leading-snug">
                          Payments secured by Stripe. Buyer Protection available on eligible purchases.
                        </p>
                      </div>
                      {!selected.is_sold && (
                        <Button className="w-full h-11 bg-dark-slate text-white rounded-full" onClick={() => toast.info('Messaging coming soon!')}>
                          <MessageCircle className="w-4 h-4 mr-2" />Message Seller
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>List an Item</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Mode Toggle — only for active advertisers */}
              {advertiserStatus?.is_active && (
                <div className="flex rounded-xl bg-slate-100 p-1">
                  <button
                    onClick={() => setCreateMode('for_sale')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createMode === 'for_sale' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
                    }`}>
                    <DollarSign className="w-4 h-4 inline mr-1" />Sell Item
                  </button>
                  <button
                    onClick={() => setCreateMode('advertisement')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                      createMode === 'advertisement' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-gray'
                    }`}>
                    <Store className="w-4 h-4 inline mr-1" />Advertise
                  </button>
                </div>
              )}

              {createMode === 'advertisement' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-xs text-purple-700">
                  Advertisement listings link visitors to your website instead of selling through the marketplace.
                </div>
              )}

              {/* Photo & Video uploads */}
              <div className="flex gap-4 justify-center">
                {/* Photo */}
                <label className="relative cursor-pointer">
                  <div className="w-32 h-32 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center overflow-hidden hover:border-mint-fresh gap-1">
                    {photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : (
                      <>
                        <Camera className="w-7 h-7 text-slate-gray" />
                        <span className="text-[10px] text-slate-gray">Photo</span>
                      </>
                    )}
                  </div>
                  {photoPreview && <button onClick={(e) => { e.preventDefault(); setItemPhoto(null); setPhotoPreview(null); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-dark-slate text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                </label>

                {/* Video */}
                <label className="relative cursor-pointer">
                  <div className="w-32 h-32 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center overflow-hidden hover:border-mint-fresh gap-1">
                    {videoPreview ? (
                      <video src={videoPreview} className="w-full h-full object-cover" muted />
                    ) : (
                      <>
                        <Video className="w-7 h-7 text-slate-gray" />
                        <span className="text-[10px] text-slate-gray">Video</span>
                      </>
                    )}
                  </div>
                  {videoPreview && <button onClick={(e) => { e.preventDefault(); setItemVideo(null); setVideoPreview(null); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-dark-slate text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>}
                  <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleVideoSelect} />
                </label>
              </div>

              <div><Label>Title *</Label><Input value={newItem.title} onChange={(e) => setNewItem(p => ({ ...p, title: e.target.value }))} placeholder={createMode === 'advertisement' ? 'Business or product name' : 'Item name'} className="mt-1" /></div>

              {createMode === 'advertisement' ? (
                <div><Label>Website URL *</Label><Input value={newItem.website_url} onChange={(e) => setNewItem(p => ({ ...p, website_url: e.target.value }))} placeholder="https://yourbusiness.com" className="mt-1" /></div>
              ) : (
                <div><Label>Price *</Label><Input type="number" value={newItem.price} onChange={(e) => setNewItem(p => ({ ...p, price: e.target.value }))} placeholder="0.00" className="mt-1" /></div>
              )}

              <div className={createMode === 'advertisement' ? '' : 'grid grid-cols-2 gap-3'}>
                <div><Label>Category</Label>
                  <Select value={newItem.category} onValueChange={(v) => setNewItem(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {createMode !== 'advertisement' && (
                  <div><Label>Condition</Label>
                    <Select value={newItem.condition} onValueChange={(v) => setNewItem(p => ({ ...p, condition: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="like_new">Like New</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div><Label>Description</Label><Textarea value={newItem.description} onChange={(e) => setNewItem(p => ({ ...p, description: e.target.value }))} rows={3} className="mt-1" placeholder={createMode === 'advertisement' ? 'Describe your business or what you offer...' : 'Describe the item...'} /></div>
              <Button onClick={handleCreate} disabled={creating} className={`w-full h-11 rounded-full ${createMode === 'advertisement' ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-dark-slate text-white'}`}>
                {creating ? 'Listing...' : createMode === 'advertisement' ? 'Create Advertisement' : 'List Item'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Marketplace;
